import { ComparatorType } from "./Constants";
import { IUserFactDataProperty, IUserFactManifest } from "../Common/Models";
import { EventIds } from "../Logging/Constants";
import * as Logger from "../Logging/Logger";
import { TelemetryEvent, DataField } from "@microsoft/oteljs";
import { getSamplingRate } from "../Common/Settings";

export function CompareDataPropertyToDataField(prop: IUserFactDataProperty, dataField: DataField): boolean {
	if (dataField.dataType != prop.type) {
		return false;
	}

	switch (prop.comparator) {
		case ComparatorType.Equal:
			return dataField.value === prop.value;
		case ComparatorType.NotEqual:
			return dataField.value != prop.value;
		case ComparatorType.GreaterThan:
			return dataField.value > prop.value;
		case ComparatorType.GreaterThanOrEqual:
			return dataField.value >= prop.value;
		case ComparatorType.LessThan:
			return dataField.value < prop.value;
		case ComparatorType.LessThanOrEqual:
			return dataField.value <= prop.value;
		default:
			return false;
	}
}

export function CompareDataPropertyToTelemetryEvent(prop: IUserFactDataProperty, event: TelemetryEvent): boolean {
	if (event.dataFields) {
		const dataField = event.dataFields.find(property => property.name === prop.propertyName);

		if (dataField) {
			return CompareDataPropertyToDataField(prop, dataField);
		}	
	}

	// if the datafield property resides as part of the EventContract object search and compare those data fields
	if (event.eventContract && event.eventContract.dataFields) {
		const eventContractData = event.eventContract.dataFields.find(property => property.name === prop.propertyName);

		if (eventContractData) {
			return CompareDataPropertyToDataField(prop, eventContractData);
		}
	}

	return false;
}

// Called when canSendUserFact is called by the host apps
export function EvaluateEvent(manifest: IUserFactManifest, eventTime: Date , lastSourceTime: Date): boolean {
	if (manifest === null || eventTime === null) {
		return false;
	}

	const dedupPassed = HasDedupIntervalPassed(manifest.dedupIntervalSeconds,  eventTime, lastSourceTime);
	if (!dedupPassed) {
		Logger.getLogger().event(EventIds.UserFactsUserFactNotSentDedupInterval, { eventName: manifest.userFactName }, getSamplingRate());
	}
	return dedupPassed;
}

// Called when canSendUserFact is called through the otel telemetry sink
export function EvaluateTelemetryEvent(manifest: IUserFactManifest, event: TelemetryEvent, eventTime: Date, lastSourceTime: Date): boolean {
	if (manifest === null || eventTime === null) {
		return false;
	}

	// check that all data properties match values in telemetry event
	if (!manifest.props.every(m => CompareDataPropertyToTelemetryEvent(m, event))) {
		Logger.getLogger().event(EventIds.UserFactsTelemetryEventManifestMismatch, { eventName: manifest.userFactName }, getSamplingRate());
		return false;
	}

	const dedupPassed = HasDedupIntervalPassed(manifest.dedupIntervalSeconds, eventTime, lastSourceTime);
	if (!dedupPassed) {
		Logger.getLogger().event(EventIds.UserFactsTelemetryEventNotSentDedupInterval, { eventName: manifest.userFactName }, getSamplingRate());
	}
	return dedupPassed;
}

/**
 * Check if the dedupInterval provided in the user fact manifest has elapsed between the eventTime and the lastSourceTime
 * @param dedupIntervalSeconds the dedup interval for the user fact, obtained from the user fact manifest
 * @param eventTime the most recent time the user fact has occured
 * @param lastSourceTime the last time the user fact has occured, pulled from local storage
 */
export function HasDedupIntervalPassed(dedupIntervalSeconds: number, eventTime: Date, lastSourceTime: Date): boolean {
	if (eventTime == null) {
		return false;
	}
	// if the last source time does not exist or is an invalid date, fact is being added for the first time
	if (!lastSourceTime) {
		return true;
	}

	// validate the the dedupIntervalSeconds has elapsed since the last sourceDateTime
	return (eventTime.getTime() - lastSourceTime.getTime() >= dedupIntervalSeconds * 1000 /*convert to milliseconds*/);
}